import React, { useEffect } from 'react';
import CustomHead from '@/components/Common/Layout/Head';
import Image from 'next/image';
import Link from 'next/link';
import { Layout } from '@/components/Common';
import { STATIC_FILE_URL, WEBSITE_URL } from '@/shared/constants';
import { businessSettingsDisplay } from '@/lib/service/common';
import { imageTransform, scrollTo } from '@/shared/utils/utils';
import { logPageView } from '@/lib/service';
import type { NextPageWithLayout } from '@/pages/_app';
import type { InferGetStaticPropsType } from 'next';
import type { OptionType } from '@/shared/types/typings';
import './index.less';
import { IPlatformPageSection, PlatformPageSectionType, PlatformPageTextGridItem } from '@/shared/types/platform-pages';
import PlatformPageSection from '@/components/Platform/PlatformPageSection';

export async function getServerSideProps() {
  const { value } = await businessSettingsDisplay<{
    JOB_OPENINGS: Array<OptionType>;
    MEDIA_COVERAGE: Array<OptionType>;
  }>('CAREERS_PAGE');
  return {
    props: {
      jobs: value.JOB_OPENINGS ?? [],
      mediaCoverage: value.MEDIA_COVERAGE ?? [],
    },
  };
}

const whyPietraItems: PlatformPageTextGridItem[] = [
  {
    icon: `${STATIC_FILE_URL}/04fa6b7649cbeb2dd4f828cff25e3a42.png`,
    title: 'Ownership',
    description: 'We trust and value every member of our team, bestowing unparalleled ownership and responsibility.',
  },
  {
    icon: `${STATIC_FILE_URL}/197e3724403e1154937ef37cde46f0c1.png`,
    title: 'Dynamic growth',
    description:
      "Be part of a team that's expanding exponentially, offering endless learning and growth opportunities.",
  },
  {
    icon: `${STATIC_FILE_URL}/bed72614168ee9fe413b8cc0699632a8.png`,
    title: 'Investor trust',
    description:
      "Our journey is fortified with trust and investment from globally-renowned names like a16z and Founder's Fund.",
  },
  {
    icon: `${STATIC_FILE_URL}/9a74a2fcd92cabb636a0d5c16518319d.png`,
    title: 'Growth-driven success',
    description:
      'Join a company that not only celebrates success but is constantly on the move, setting new benchmarks and expanding horizons.',
  },
];

const coreValuesItems: PlatformPageTextGridItem[] = [
  {
    icon: `${STATIC_FILE_URL}/a5a04f330d191fd27bf183d660dd9abb.png`,
    title: 'Customer empowerment',
    description:
      'Strives to build a set of products and services that empowers our customers through technology and entrepreneurship.',
  },
  {
    icon: `${STATIC_FILE_URL}/04fa6b7649cbeb2dd4f828cff25e3a42.png`,
    title: 'Ownership',
    description:
      'Acts with accountability for their role and the outcomes of their personal, team, and company efforts.',
  },
  {
    icon: `${STATIC_FILE_URL}/3247973b8cbe9925fc9d806272914fa9.png`,
    title: 'Trust and collaboration',
    description:
      'Trusts in the team to execute at a high level while striving to always offer a helping hand for the means of community.',
  },
  {
    icon: `${STATIC_FILE_URL}/92b6a9d55e3651ef2df9a82236fed3d9.png`,
    title: 'Speed of execution',
    description: 'Uses agility and flexibility to tackle ambiguity and decisively execute on their goals.',
  },
];

const benefitItems: PlatformPageTextGridItem[] = [
  {
    icon: `${STATIC_FILE_URL}/80d8fef1cd0f575f4fe5d74590747342.png`,
    title: 'Health insurance',
    description:
      'Comprehensive insurance plans to keep you and your loved ones healthy. Medical, Vision, Dental and FSA for you and your dependents.',
  },
  {
    icon: `${STATIC_FILE_URL}/525dbf64a18d7e00916eadb97f8dcb44.png`,
    title: '401k Plans',
    description: 'Build a future with our retirement and saving options.',
  },
  {
    icon: `${STATIC_FILE_URL}/5d7a6232e0b2aef7d7f2e220e9f84277.png`,
    title: 'Paid family leave',
    description:
      'Time off to care for your family, so you can focus on what matters most during important life events.',
  },
  {
    icon: `${STATIC_FILE_URL}/d40835a8abbbdd16898a89ced720b38a.png`,
    title: 'More benefits',
    description:
      'Beyond the essentials, we offer a range of extra perks designed to make life easier and more enjoyable including commuter benefits, paid vacation, team trips, and team dinners.',
  },
];

const JOB_POSTINGS_CONTAINER = 'job-postings-container';
const careerPageSections: IPlatformPageSection[] = [
  {
    type: PlatformPageSectionType.TEXT_IMAGE,
    preTitle: 'Careers at Pietra',
    preTitleColor: '#fff',
    title: 'Work with us',
    titleColor: '#fff',
    description: 'Join Pietra and build the future of e-commerce.',
    descriptionColor: 'rgba(255,255,255, 0.8)',
    bgColor: '#ff5c3c',
    buttons: [
      {
        btnText: 'Join us',
        onClick: () => scrollTo(JOB_POSTINGS_CONTAINER),
      },
    ],
    image: `${STATIC_FILE_URL}/5919b139aad78d98822e624ae7c9f442.png`,
  },
  {
    type: PlatformPageSectionType.CALL_TO_ACTION,
    preTitle: '',
    preTitleColor: '',
    title: 'Our mission',
    titleColor: '#000000',
    description:
      'At Pietra, we are building the fastest, easiest, and most profitable way to start and scale your e-commerce brand.',
    descriptionColor: 'rgba(0, 0, 0, 0.6)',
    bgColor: '#F9F2F1',
    buttons: [
      {
        btnText: 'Explore opportunities',
        onClick: () => scrollTo(JOB_POSTINGS_CONTAINER),
      },
    ],
  },
  {
    type: PlatformPageSectionType.TEXT_GRID,
    preTitle: 'Be part of something big',
    preTitleColor: '#ff5c3c',
    title: 'Why Pietra?',
    titleColor: '#000000',
    description: 'Unlocking exceptional opportunities for growth and impact.',
    descriptionColor: 'rgba(0, 0, 0, 0.6)',
    bgColor: '#ffffff',
    buttons: [
      {
        btnText: 'Explore opportunities',
        onClick: () => scrollTo(JOB_POSTINGS_CONTAINER),
      },
    ],
    textGridItems: whyPietraItems,
  },
  {
    type: PlatformPageSectionType.TEXT_IMAGE,
    preTitle: 'Our team',
    preTitleColor: '#000000',
    title: 'Who we are',
    titleColor: '#000000',
    description:
      "We pride ourselves on a culture where individuality meets teamwork. We're a dedicated group of thinkers, creators, and doers. Together, we're pushing the boundaries of what's possible in e-commerce.",
    descriptionColor: 'rgba(0, 0, 0, 0.6)',
    bgColor: '#F9F2F1',
    buttons: [
      {
        btnText: 'Join us',
        onClick: () => scrollTo(JOB_POSTINGS_CONTAINER),
      },
    ],
    image: `${STATIC_FILE_URL}/ffe3db3a4d35415b915faa938296c229.png`,
  },
  {
    type: PlatformPageSectionType.TEXT_GRID,
    preTitle: 'Roots of our culture',
    preTitleColor: '#ff5c3c',
    title: 'Core values',
    titleColor: '#000000',
    description: 'Unlocking exceptional opportunities for growth and impact.',
    descriptionColor: 'rgba(0, 0, 0, 0.6)',
    bgColor: '#ffffff',
    buttons: [
      {
        btnText: 'Join us',
        onClick: () => scrollTo(JOB_POSTINGS_CONTAINER),
      },
    ],
    textGridItems: coreValuesItems,
  },
  {
    type: PlatformPageSectionType.TEXT_GRID,
    preTitle: 'Investing in you',
    preTitleColor: '#ff5c3c',
    title: 'Our benefits',
    titleColor: '#000000',
    description:
      'At Pietra, we understand that personal well-being leads to professional excellence. Explore the comprehensive benefits that make working here a truly rewarding experience.',
    descriptionColor: 'rgba(0, 0, 0, 0.6)',
    bgColor: '#F9F2F1',
    buttons: [
      {
        btnText: 'Explore opportunities',
        onClick: () => scrollTo(JOB_POSTINGS_CONTAINER),
      },
    ],
    textGridItems: benefitItems,
  },
];

const CareersPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = ({
  jobs,
  mediaCoverage,
}) => {
  useEffect(() => {
    logPageView({
      pageUrl: window.location.href || '',
      routeName: window.location.pathname,
      pageTitle: 'Careers at Pietra',
    });
  }, []);

  return (
    <div className="careers-page">
      {careerPageSections.map((section: IPlatformPageSection) => (
        <PlatformPageSection key={section.title} content={section} />
      ))}
      <div className="content-body" id={JOB_POSTINGS_CONTAINER}>
        <div className="body-content">
          <p className="title">Current Openings</p>
          {jobs.length > 0 ? (
            <div className="list">
              {jobs.map((job, index) => (
                <Link className="list-item" target="_blank" href={job.url} key={`list-item-${index}`}>
                  {job.title}
                  <div className="apply">
                    <span>Apply</span>
                    <Image
                      className="arrow"
                      src="/images/carousel_next.png"
                      alt="Next Slide"
                      title="Next Slide"
                      loading="lazy"
                      width={14}
                      height={14}
                    />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p>There are currently no openings.</p>
          )}
        </div>
        <div className="body-content">
          <p className="title">Read About Us... Everywhere!</p>
          <div className="list">
            {mediaCoverage.map((item, index) => (
              <Link className="list-item" target="_blank" href={item.url} key={`media-item-${index}`}>
                <div>
                  {item.title}
                  <span className="apply">
                    <Image
                      className="arrow"
                      src="/images/carousel_next.png"
                      alt="Next Slide"
                      title="Next Slide"
                      loading="lazy"
                      width={14}
                      height={14}
                    />
                  </span>
                </div>
                {item.logo && (
                  <Image
                    className="media-item-logo"
                    src={imageTransform(item.logo, 'compact')}
                    alt="Award Logo"
                    title="Award Logo"
                    width={600}
                    height={24}
                  />
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

CareersPage.getLayout = (page: React.ReactElement) => {
  return (
    <>
      <CustomHead
        title="Careers at Pietra"
        description="Join Pietra and build the future of Creator e-commerce. We're looking for highly motivated, unconventional thinkers to join our mission."
        url={`${WEBSITE_URL}/careers`}
      />
      <Layout>{page}</Layout>
    </>
  );
};

export default CareersPage;
